import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Check, X, Coffee, Ban, Users, Car, Clock, Wrench, Filter, ChevronDown, UserX } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";

const JOURS_FR = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const JOURS_COURT = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const statusConfig = {
  travaille: { label: "Travaille", icon: Check, cls: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "bg-emerald-500" },
  non_remunere: { label: "Non remunere", icon: Coffee, cls: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-500" },
  repos: { label: "Repos", icon: Coffee, cls: "bg-slate-100 text-slate-600 border-slate-300", dot: "bg-slate-400" },
  absent: { label: "Absent", icon: X, cls: "bg-red-100 text-red-800 border-red-300", dot: "bg-red-500" },
  immobilise: { label: "Immobilise", icon: Ban, cls: "bg-violet-100 text-violet-800 border-violet-300", dot: "bg-violet-500" },
  garage: { label: "Garage", icon: Wrench, cls: "bg-orange-100 text-orange-800 border-orange-300", dot: "bg-orange-500" },
  sans_chauffeur: { label: "Sans chauffeur", icon: UserX, cls: "bg-rose-100 text-rose-800 border-rose-300", dot: "bg-rose-500" },
};

function getMonday(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split("T")[0];
}

function formatDateShort(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export default function PlanningPage() {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [planning, setPlanning] = useState([]);
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("day");
  const [showModify, setShowModify] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [modifyStatut, setModifyStatut] = useState("");
  const [modifyNote, setModifyNote] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState([]);

  const fetchPlanning = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/planning?date=${date}`);
      setPlanning(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchWeek = async () => {
    setLoading(true);
    try {
      const monday = getMonday(date);
      const res = await api.get(`/planning/week?start_date=${monday}`);
      setWeekData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (viewMode === "day") fetchPlanning();
    else fetchWeek();
  }, [date, viewMode]);

  const changeDate = (delta) => {
    const d = new Date(date + "T12:00:00");
    d.setDate(d.getDate() + (viewMode === "week" ? delta * 7 : delta));
    setDate(d.toISOString().split("T")[0]);
  };

  const handleModify = async () => {
    try {
      await api.post("/planning/modify", { date: selectedEntry.modifyDate || date, vehicule_id: selectedEntry.vehicule_id || selectedEntry.vehicule?.id, chauffeur_id: selectedEntry.chauffeur_id || selectedEntry.chauffeur?.id, statut: modifyStatut, note: modifyNote });
      toast.success("Planning modifie");
      setShowModify(false);
      if (viewMode === "day") fetchPlanning();
      else fetchWeek();
    } catch (e) { toast.error("Erreur"); }
  };

  const d = new Date(date + "T12:00:00");
  const dateFormatted = d.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  // Extract unique vehicles from planning data
  const allVehicles = (() => {
    const vMap = {};
    const source = viewMode === "day" ? planning : (weekData?.planning || []);
    source.forEach(p => {
      const v = p.vehicule;
      if (v && v.id && !vMap[v.id]) {
        vMap[v.id] = `${v.marque || ""} ${v.modele || ""} (${v.immatriculation || ""})`.trim();
      }
    });
    return Object.entries(vMap).sort((a, b) => a[1].localeCompare(b[1]));
  })();

  const toggleVehicle = (vid) => {
    setSelectedVehicles(prev =>
      prev.includes(vid) ? prev.filter(v => v !== vid) : [...prev, vid]
    );
  };

  // Filtered data
  const filteredPlanning = selectedVehicles.length === 0
    ? planning
    : planning.filter(p => selectedVehicles.includes(p.vehicule_id || p.vehicule?.id));

  const filteredWeekPlanning = selectedVehicles.length === 0
    ? (weekData?.planning || [])
    : (weekData?.planning || []).filter(p => selectedVehicles.includes(p.vehicule?.id));

  // Daily stats (use filtered data)
  const stats = {
    travaille: filteredPlanning.filter(p => p.statut === "travaille").length,
    non_remunere: filteredPlanning.filter(p => p.statut === "non_remunere").length,
    repos: filteredPlanning.filter(p => p.statut === "repos").length,
    absent: filteredPlanning.filter(p => p.statut === "absent").length,
    immobilise: filteredPlanning.filter(p => p.statut === "immobilise").length,
    garage: filteredPlanning.filter(p => p.statut === "garage").length,
    total_recette: filteredPlanning.reduce((s, p) => s + (p.recette_attendue || 0), 0),
    total_paye: filteredPlanning.reduce((s, p) => s + (p.recette_payee || 0), 0),
    enregistrees: filteredPlanning.filter(p => p.recette_enregistree).length,
  };

  return (
    <div data-testid="planning-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Planning</h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">{viewMode === "day" ? dateFormatted : `Semaine du ${formatDateShort(getMonday(date))}`}</p>
        </div>
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList>
            <TabsTrigger value="day" data-testid="tab-day"><CalIcon className="w-4 h-4 mr-1" />Jour</TabsTrigger>
            <TabsTrigger value="week" data-testid="tab-week"><Clock className="w-4 h-4 mr-1" />Semaine</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Date Navigation */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" onClick={() => changeDate(-1)} data-testid="btn-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <div className="flex items-center gap-3">
              <CalIcon className="w-5 h-5 text-[#0F52BA]" />
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-auto" data-testid="input-date" />
            </div>
            <Button variant="outline" size="icon" onClick={() => changeDate(1)} data-testid="btn-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Vehicle Multi-Select Filter */}
      {allVehicles.length > 0 && (
        <div className="flex items-center gap-3 mb-6" data-testid="vehicle-filter">
          <Filter className="w-4 h-4 text-slate-400" />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="min-w-[240px] justify-between" data-testid="btn-vehicle-filter">
                <span className="truncate">
                  {selectedVehicles.length === 0
                    ? "Tous les vehicules"
                    : `${selectedVehicles.length} vehicule(s) selectionne(s)`}
                </span>
                <ChevronDown className="w-4 h-4 ml-2 shrink-0 text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-2 max-h-[300px] overflow-y-auto" align="start">
              <div
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm font-medium text-[#0F52BA]"
                onClick={() => setSelectedVehicles([])}
                data-testid="btn-clear-vehicle-filter"
              >
                <Car className="w-4 h-4" />
                Tous les vehicules
              </div>
              <div className="border-t my-1"></div>
              {allVehicles.map(([vid, vname]) => (
                <label
                  key={vid}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer text-sm"
                  data-testid={`filter-vehicle-${vid}`}
                >
                  <Checkbox
                    checked={selectedVehicles.includes(vid)}
                    onCheckedChange={() => toggleVehicle(vid)}
                  />
                  <span className="truncate">{vname}</span>
                </label>
              ))}
            </PopoverContent>
          </Popover>
          {selectedVehicles.length > 0 && (
            <Button variant="ghost" size="sm" className="text-slate-500 text-xs" onClick={() => setSelectedVehicles([])} data-testid="btn-reset-filter">
              Reinitialiser
            </Button>
          )}
        </div>
      )}

      {/* Summary Cards - Day view only */}
      {viewMode === "day" && !loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="planning-stats">
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><Users className="w-4 h-4 text-emerald-600" /></div></div>
            <p className="text-xl font-bold text-emerald-600 font-heading">{stats.travaille}</p>
            <p className="text-xs text-slate-500">Au travail</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><Coffee className="w-4 h-4 text-amber-600" /></div></div>
            <p className="text-xl font-bold text-amber-600 font-heading">{stats.repos + stats.non_remunere}</p>
            <p className="text-xs text-slate-500">Repos / Non rem.</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Car className="w-4 h-4 text-blue-600" /></div></div>
            <p className="text-xl font-bold text-blue-600 font-heading">{stats.total_recette.toLocaleString("fr-FR")} Frc</p>
            <p className="text-xs text-slate-500">Recette attendue</p>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center"><Check className="w-4 h-4 text-slate-600" /></div></div>
            <p className="text-xl font-bold text-slate-700 font-heading">{stats.enregistrees}/{planning.length}</p>
            <p className="text-xs text-slate-500">Recettes enregistrees</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-500 text-center py-12">Chargement du planning...</div>
      ) : viewMode === "day" ? (
        /* ========== DAY VIEW ========== */
        <div className="table-container">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                <TableHead className="font-semibold text-slate-600">Chauffeur</TableHead>
                <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                <TableHead className="font-semibold text-slate-600">Horaires</TableHead>
                <TableHead className="font-semibold text-slate-600">Recette attendue</TableHead>
                <TableHead className="font-semibold text-slate-600">Recette payee</TableHead>
                <TableHead className="font-semibold text-slate-600">Note</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlanning.map((p, i) => {
                const st = statusConfig[p.statut] || statusConfig.travaille;
                const Icon = st.icon;
                return (
                  <TableRow key={i} data-testid={`planning-row-${i}`}>
                    <TableCell className="font-medium text-slate-900">{p.vehicule?.marque} {p.vehicule?.modele}<br /><span className="text-xs text-slate-500">{p.vehicule?.immatriculation}</span></TableCell>
                    <TableCell className="text-slate-700">{p.chauffeur?.prenom} {p.chauffeur?.nom}</TableCell>
                    <TableCell><Badge variant="outline" className={st.cls}><Icon className="w-3 h-3 mr-1" />{st.label}</Badge></TableCell>
                    <TableCell className="text-slate-600 text-sm">{p.plage_horaire}</TableCell>
                    <TableCell className="text-slate-600">{p.recette_attendue} Frc</TableCell>
                    <TableCell>{p.recette_enregistree
                      ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{p.recette_payee} Frc</Badge>
                      : p.recette_attendue === 0
                        ? <Badge variant="outline" className="text-slate-400 border-slate-200 bg-slate-50">Non applicable</Badge>
                        : <Badge variant="outline" className="text-amber-600 border-amber-200">En attente</Badge>
                    }</TableCell>
                    <TableCell className="text-slate-500 text-sm max-w-[120px] truncate">{p.note || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-[#0F52BA]" onClick={() => { setSelectedEntry(p); setModifyStatut(p.statut); setModifyNote(p.note || ""); setShowModify(true); }} data-testid={`btn-modify-${i}`}>
                        Modifier
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredPlanning.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12">Aucune activite prevue pour ce jour</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      ) : (
        /* ========== WEEK VIEW ========== */
        weekData && (
          <div className="table-container overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600 min-w-[180px] sticky left-0 bg-slate-50/50 z-10">Chauffeur / Vehicule</TableHead>
                  {weekData.dates.map(dt => {
                    const wd = new Date(dt + "T12:00:00");
                    const isToday = dt === new Date().toISOString().split("T")[0];
                    return (
                      <TableHead key={dt} className={`font-semibold text-center min-w-[100px] ${isToday ? "bg-blue-50 text-blue-700" : "text-slate-600"}`}>
                        <div className="text-xs">{JOURS_COURT[wd.getDay()]}</div>
                        <div className="text-sm">{wd.getDate()}/{wd.getMonth() + 1}</div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWeekPlanning.map((row, i) => (
                  <TableRow key={i} data-testid={`week-row-${i}`}>
                    <TableCell className="sticky left-0 bg-white z-10 border-r">
                      <div className="font-medium text-slate-900 text-sm">{row.chauffeur?.prenom} {row.chauffeur?.nom}</div>
                      <div className="text-xs text-slate-500">{row.vehicule?.immatriculation} - {row.vehicule?.marque}</div>
                    </TableCell>
                    {row.days.map((day, j) => {
                      const st = statusConfig[day.statut] || statusConfig.travaille;
                      const isToday = day.date === new Date().toISOString().split("T")[0];
                      return (
                        <TableCell key={j} className={`text-center p-1.5 ${isToday ? "bg-blue-50/50" : ""}`}
                          onClick={() => {
                            setSelectedEntry({ ...row, modifyDate: day.date });
                            setModifyStatut(day.statut);
                            setModifyNote("");
                            setShowModify(true);
                          }}
                        >
                          <div className="cursor-pointer rounded-lg p-2 hover:ring-2 hover:ring-[#0F52BA]/20 transition-all" data-testid={`week-cell-${i}-${j}`}>
                            <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${st.dot}`}></div>
                            <div className="text-[10px] text-slate-500">{st.label.slice(0, 5)}</div>
                            {day.statut === "travaille" && (
                              <div className="text-[10px] font-medium mt-0.5">
                                {day.recette_enregistree
                                  ? <span className="text-emerald-600">{day.recette_payee} Frc</span>
                                  : <span className="text-slate-400">{day.recette_attendue} Frc</span>
                                }
                              </div>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {filteredWeekPlanning.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12">Aucune affectation active</TableCell></TableRow>}
              </TableBody>
            </Table>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 mt-4 px-2">
              {Object.entries(statusConfig).map(([key, val]) => (
                <div key={key} className="flex items-center gap-1.5 text-xs text-slate-600">
                  <div className={`w-2.5 h-2.5 rounded-full ${val.dot}`}></div>
                  {val.label}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Modify Dialog */}
      <Dialog open={showModify} onOpenChange={setShowModify}>
        <DialogContent data-testid="modify-planning-dialog">
          <DialogHeader><DialogTitle className="font-heading">Modifier le planning</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedEntry && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <p><span className="text-slate-500">Chauffeur :</span> <span className="font-medium">{selectedEntry.chauffeur?.prenom} {selectedEntry.chauffeur?.nom}</span></p>
                <p><span className="text-slate-500">Vehicule :</span> <span className="font-medium">{selectedEntry.vehicule?.immatriculation}</span></p>
                <p><span className="text-slate-500">Date :</span> <span className="font-medium">{selectedEntry.modifyDate ? new Date(selectedEntry.modifyDate + "T12:00:00").toLocaleDateString("fr-FR") : new Date(date + "T12:00:00").toLocaleDateString("fr-FR")}</span></p>
              </div>
            )}
            <div>
              <Label>Statut</Label>
              <Select value={modifyStatut} onValueChange={setModifyStatut}>
                <SelectTrigger data-testid="select-modify-statut"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="travaille">Travaille</SelectItem>
                  <SelectItem value="repos">Repos</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="non_remunere">Non remunere</SelectItem>
                  <SelectItem value="garage">Garage</SelectItem>
                  <SelectItem value="sans_chauffeur">Sans chauffeur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Note</Label><Input value={modifyNote} onChange={e => setModifyNote(e.target.value)} placeholder="Raison de la modification..." data-testid="input-modify-note" /></div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModify(false)}>Annuler</Button>
              <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={handleModify} data-testid="btn-submit-modify">Confirmer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
